import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ChangeDetectorRef,
  OnChanges,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

@Component({
  selector: 'kt-autocomplete-select',
  templateUrl: 'autocomplete-select.component.html',
  styleUrls: ['autocomplete-select.component.scss'],
  standalone: false,
})
export class AutocompleteSelectComponent implements OnInit, OnChanges {
  @ViewChild('auto', { static: true }) autocomplete: MatAutocomplete;
  inputControl = new FormControl(null, {
    validators: Validators.required,
  });
  @Input() shouldPrioritizePreFix: boolean;
  @Input() value: string;
  @Input() label: string;
  @Input() appearance: MatFormFieldAppearance;
  @Input() options: OptionItem[];
  @Input() errorMessage: string;
  @Input() excludeValue: string;
  @Output()
  onValueChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  onValueSelected: EventEmitter<any> = new EventEmitter<any>();
  filteredOptions: Observable<OptionItem[]>;
  @Output()
  onInputBlured: EventEmitter<any> = new EventEmitter<any>();

  constructor(private ref: ChangeDetectorRef) {}

  ngOnInit() {
    if (this.options) {
      this.onInitElements();
    }
    if (this.value) {
      this.inputControl.setValue({ name: this.value });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.options && changes.options.currentValue) {
      this.onInitElements();
    }
    if (changes.value) {
      if (!changes.value.currentValue) {
        this.inputControl.markAsTouched();
        this.inputControl.setErrors({ required: true });
      } else if (!changes.value.currentValue.name) {
        this.inputControl.setValue({ name: changes.value.currentValue });
      }
      this.ref.detectChanges();
    }
  }

  onTouchInput(): void {
    if (!this.value) {
      this.inputControl.setErrors({ required: true });
    }
  }

  onInitElements(): void {
    this.filteredOptions = this.inputControl.valueChanges.pipe(
      filter((value) => (value.length > 1 ? value.length > 1 : value)),
      map((value) => (typeof value === 'string' ? value : value.name)),
      map((name) => (name ? this.filter(name) : this.options.slice())),
    );

    this.inputControl.valueChanges.subscribe((selectedValue) => {
      if (selectedValue.name) {
        this.onValueChanged.emit(selectedValue);
      }
    });
  }

  displayFn(option?: OptionItem): string | undefined {
    return option ? option.name : undefined;
  }

  filter(name: string): OptionItem[] {
    const filterValue = name.toLowerCase(),
      excludedResults = this.options.filter(
        (option) => option.value !== this.excludeValue,
      );
    if (this.shouldPrioritizePreFix) {
      const firstPartResults = excludedResults.filter((option) =>
          option.name.split('-')?.[0].toLocaleLowerCase().includes(filterValue),
        ),
        secondPartResults = excludedResults.filter((option) =>
          option.name.split('-')?.[1].toLocaleLowerCase().includes(filterValue),
        );

      return firstPartResults.concat(
        secondPartResults.filter((value) => !firstPartResults.includes(value)),
      );
    }
    return excludedResults.filter((option) =>
      option.name.toLocaleLowerCase().includes(filterValue),
    );
  }

  onInputBlur() {
    if (this.inputControl.value) {
      this.onInputBlured.emit({
        id: null,
        name: this.inputControl.value,
        value: this.inputControl.value.name
          ? this.inputControl.value.name
          : this.inputControl.value,
      });
    } else {
      this.onInputBlured.emit(null);
    }
  }
  onSelectOption(event): void {
    this.inputControl.setValue(event.option.value);
    this.onValueSelected.emit(this.inputControl.value);
  }
}

interface OptionItem {
  name: string;
  value?: any;
}
